import {
  IconChevronRight,
  IconCpu,
  IconHistory,
  IconHome,
  IconStar,
  IconTags,
  IconVideo,
} from '@tabler/icons-react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';
import { getCategoryIcon } from '@/components/icons/category-icon';
import { getModelIcon } from '@/components/icons/model-icon';
import {
  isToolGroupActive,
  isToolNavItemActive,
  TOOL_NAV_GROUPS,
} from '@/components/navigation/tool-navigation';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  mergeImagePromptSearch,
  sanitizeImagePromptSearch,
} from '@/hooks/use-image-prompts-search';
import {
  IMAGE_PROMPT_CATEGORIES,
  type ImagePromptCategory,
  type ImagePromptModel,
} from '@/lib/image-prompts';
import { getLocale, localizeHref, type Locale } from '@/lib/locale';
import { Routes } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { m } from '@/locale/paraglide/messages';

function getModelFilters(): { value: ImagePromptModel; label: string }[] {
  return [
    { value: 'nanobanana', label: 'Nano Banana' },
    { value: 'gptimage', label: 'GPT Image' },
  ];
}

const MODEL_PAGE_LINKS = [
  {
    key: 'minimax-h3',
    href: Routes.ReferenceToVideo,
    label: 'MiniMax H3',
    icon: IconVideo,
  },
] as const;

const SIDEBAR_MAIN_COPY = {
  en: {
    aiImageEditor: 'AI image editor',
    aiImageUpscaler: 'AI image upscaler',
    imageStitcher: 'Image stitcher',
    imageAi: 'Image AI',
    imageToVideo: 'Image to Video',
    referenceToVideo: 'Reference to Video',
    prompts: 'Prompts',
    textToVideo: 'Text to Video',
    videoEditor: 'AI Video Editor',
    videoUpscaler: 'AI Video Upscaler',
    videoAi: 'Video AI',
  },
  es: {
    aiImageEditor: 'Editor de imagenes IA',
    aiImageUpscaler: 'Escalador de imagen IA',
    imageStitcher: 'Unidor de imágenes',
    imageAi: 'Imagen IA',
    imageToVideo: 'Imagen a video',
    referenceToVideo: 'Referencia a video',
    prompts: 'Prompts',
    textToVideo: 'Texto a video',
    videoEditor: 'Editor de video IA',
    videoUpscaler: 'Escalador de video IA',
    videoAi: 'Video IA',
  },
} as const satisfies Record<Locale, Record<string, string>>;

function getSidebarMainCopy(locale: Locale) {
  return SIDEBAR_MAIN_COPY[locale] ?? SIDEBAR_MAIN_COPY.en;
}

function getLocalizedSidebarToolGroups(
  copy: ReturnType<typeof getSidebarMainCopy>
) {
  const localizeItem = (
    item: (typeof TOOL_NAV_GROUPS)[number]['items'][number]
  ): (typeof TOOL_NAV_GROUPS)[number]['items'][number] => ({
    ...item,
    label:
      item.key === 'image-to-video'
        ? copy.imageToVideo
        : item.key === 'reference-to-video'
          ? copy.referenceToVideo
          : item.key === 'video-editor'
            ? copy.videoEditor
            : item.key === 'text-to-video'
              ? copy.textToVideo
              : item.key === 'video-upscale'
                ? copy.videoUpscaler
                : item.key === 'image-editor'
                  ? copy.aiImageEditor
                  : item.key === 'image-upscale'
                    ? copy.aiImageUpscaler
                    : item.key === 'image-stitch'
                      ? copy.imageStitcher
                      : item.label,
    children: item.children?.map(localizeItem),
  });

  return TOOL_NAV_GROUPS.map((group) => ({
    ...group,
    label:
      group.key === 'video-ai'
        ? copy.videoAi
        : group.key === 'image-ai'
          ? copy.imageAi
          : group.label,
    items: group.items.map(localizeItem),
  }));
}

function getPrimaryNav(locale: Locale) {
  return [
    {
      key: 'home',
      label: m.common_home({}, { locale }),
      icon: IconHome,
      to: Routes.Root,
    },
    {
      key: 'favorites',
      label: m.favorites_title({}, { locale }),
      icon: IconStar,
      to: Routes.Favorites,
    },
    {
      key: 'history',
      label: m.history_title({}, { locale }),
      icon: IconHistory,
      to: Routes.History,
    },
  ] as const;
}

function getCategoryTitle(slug: ImagePromptCategory, locale: Locale) {
  switch (slug) {
    case 'all':
      return m.gallery_all_prompts({}, { locale });
    case 'food-drink':
      return m.gallery_category_food_drink({}, { locale });
    case 'illustration-3d':
      return m.gallery_category_illustration_3d({}, { locale });
    case 'photography':
      return m.gallery_category_photography({}, { locale });
    case 'poster-design':
      return m.gallery_category_poster_design({}, { locale });
    case 'product-brand':
      return m.gallery_category_product_brand({}, { locale });
    case 'ui-graphic':
      return m.gallery_category_ui_graphic({}, { locale });
    default:
      return slug;
  }
}

/** Sidebar nav plus gallery category shortcuts. */
export function SidebarMain() {
  const locale = getLocale();
  const { isMobile, setOpenMobile } = useSidebar();
  const pathname = useRouterState({ select: (s) => s.location.pathname }) ?? '';
  const copy = getSidebarMainCopy(locale);
  const toolGroups = getLocalizedSidebarToolGroups(copy);
  const primaryNav = getPrimaryNav(locale);
  const modelFilters = getModelFilters();
  // Keep the canonical home entry distinct from filtered gallery URLs.
  const rawSearch = useRouterState({ select: (s) => s.location.search }) as
    | { category?: string; model?: string; sort?: string; q?: string }
    | undefined;
  const search = sanitizeImagePromptSearch(rawSearch);

  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [modelsOpen, setModelsOpen] = useState(false);
  const [openToolGroups, setOpenToolGroups] = useState<Record<string, boolean>>(
    () => Object.fromEntries(TOOL_NAV_GROUPS.map((group) => [group.key, true]))
  );
  const [openToolItems, setOpenToolItems] = useState<Record<string, boolean>>(
    {}
  );

  const isHome = pathname === Routes.Root;
  const activeCategory = isHome ? (search?.category ?? 'all') : null;
  const activeModel = isHome ? (search?.model ?? 'all') : null;
  const showCategoriesSub =
    categoriesOpen || (activeCategory !== null && activeCategory !== 'all');
  const isModelsPage = MODEL_PAGE_LINKS.some((item) => pathname === item.href);
  const showModelsSub =
    modelsOpen ||
    isModelsPage ||
    (activeModel !== null && activeModel !== 'all');

  const closeMobileSidebar = () => {
    if (isMobile) setOpenMobile(false);
  };
  const getLocalizedHref = (href: string) => localizeHref(href, { locale });

  return (
    <>
      <SidebarGroup>
        <SidebarGroupContent className="flex flex-col gap-0.5">
          <SidebarMenu>
            {primaryNav.map((item) => {
              const Icon = item.icon;
              const isHomeItem = item.key === 'home';
              // Any active gallery filter means this is not the canonical home view.
              const isActive = isHomeItem
                ? isHome &&
                  !search?.category &&
                  !search?.model &&
                  !search?.sort &&
                  !search?.q
                : pathname === item.to;
              return (
                <SidebarMenuItem key={item.key}>
                  {isHomeItem ? (
                    <SidebarMenuButton
                      isActive={isActive}
                      render={
                        <a
                          href={getLocalizedHref(item.to)}
                          onClick={closeMobileSidebar}
                        >
                          <Icon className="size-4 shrink-0" />
                          <span className="truncate font-medium text-sm">
                            {item.label}
                          </span>
                        </a>
                      }
                    />
                  ) : (
                    <SidebarMenuButton
                      isActive={isActive}
                      render={
                        <Link
                          to={item.to}
                          search={{}}
                          onClick={closeMobileSidebar}
                        />
                      }
                    >
                      <Icon className="size-4 shrink-0" />
                      <span className="truncate font-medium text-sm">
                        {item.label}
                      </span>
                    </SidebarMenuButton>
                  )}
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      <SidebarGroup>
        <SidebarGroupContent className="flex flex-col gap-0.5">
          <SidebarMenu>
            {toolGroups.map((group) => {
              const GroupIcon = group.icon;
              const isActive = isToolGroupActive(group, pathname);
              const showToolSub = openToolGroups[group.key] || isActive;
              return (
                <SidebarMenuItem key={group.key}>
                  <SidebarMenuButton
                    isActive={isActive}
                    onClick={() =>
                      setOpenToolGroups((current) => ({
                        ...current,
                        [group.key]: !current[group.key],
                      }))
                    }
                  >
                    <GroupIcon className="size-4 shrink-0" />
                    <span className="truncate font-medium text-sm">
                      {group.label}
                    </span>
                    <IconChevronRight
                      className={cn(
                        'ml-auto size-4 shrink-0 text-muted-foreground transition-transform',
                        showToolSub && 'rotate-90'
                      )}
                    />
                  </SidebarMenuButton>

                  {showToolSub && (
                    <SidebarMenuSub>
                      {group.items.map((item) => {
                        const ItemIcon = item.icon;
                        const hasChildren = Boolean(item.children?.length);
                        const itemIsActive = isToolNavItemActive(
                          item,
                          pathname
                        );
                        const showChildren =
                          openToolItems[item.key] ?? itemIsActive;
                        return (
                          <SidebarMenuSubItem key={item.key}>
                            <div className="flex min-w-0 items-center">
                              <SidebarMenuSubButton
                                className="flex-1"
                                isActive={itemIsActive}
                                href={getLocalizedHref(item.href)}
                                onClick={closeMobileSidebar}
                              >
                                <ItemIcon
                                  aria-hidden="true"
                                  className="size-4 shrink-0 text-muted-foreground"
                                />
                                <span className="flex-1 truncate">
                                  {item.label}
                                </span>
                              </SidebarMenuSubButton>
                              {hasChildren ? (
                                <button
                                  type="button"
                                  aria-expanded={showChildren}
                                  aria-label={`${showChildren ? 'Collapse' : 'Expand'} ${item.label}`}
                                  className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring group-data-[collapsible=icon]:hidden"
                                  onClick={() =>
                                    setOpenToolItems((current) => ({
                                      ...current,
                                      [item.key]: !(
                                        current[item.key] ?? itemIsActive
                                      ),
                                    }))
                                  }
                                >
                                  <IconChevronRight
                                    aria-hidden="true"
                                    className={cn(
                                      'size-4 transition-transform',
                                      showChildren && 'rotate-90'
                                    )}
                                  />
                                </button>
                              ) : null}
                            </div>
                            {showChildren && item.children ? (
                              <SidebarMenuSub className="ml-4 mr-0 pr-0">
                                {item.children.map((child) => {
                                  const ChildIcon = child.icon;
                                  return (
                                    <SidebarMenuSubItem key={child.key}>
                                      <SidebarMenuSubButton
                                        isActive={pathname === child.href}
                                        href={getLocalizedHref(child.href)}
                                        onClick={closeMobileSidebar}
                                      >
                                        <ChildIcon
                                          aria-hidden="true"
                                          className="size-4 shrink-0 text-muted-foreground"
                                        />
                                        <span className="flex-1 truncate">
                                          {child.label}
                                        </span>
                                      </SidebarMenuSubButton>
                                    </SidebarMenuSubItem>
                                  );
                                })}
                              </SidebarMenuSub>
                            ) : null}
                          </SidebarMenuSubItem>
                        );
                      })}
                    </SidebarMenuSub>
                  )}
                </SidebarMenuItem>
              );
            })}

            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={
                  isModelsPage ||
                  (activeModel !== null && activeModel !== 'all')
                }
                onClick={() => setModelsOpen((value) => !value)}
              >
                <IconCpu className="size-4 shrink-0" />
                <span className="truncate font-medium text-sm">
                  {m.nav_models_title({}, { locale })}
                </span>
                <IconChevronRight
                  className={cn(
                    'ml-auto size-4 shrink-0 text-muted-foreground transition-transform',
                    showModelsSub && 'rotate-90'
                  )}
                />
              </SidebarMenuButton>

              {showModelsSub && (
                <SidebarMenuSub>
                  {MODEL_PAGE_LINKS.map((item) => {
                    const Icon = item.icon;
                    return (
                      <SidebarMenuSubItem key={item.key}>
                        <SidebarMenuSubButton
                          isActive={pathname === item.href}
                          href={getLocalizedHref(item.href)}
                          onClick={closeMobileSidebar}
                        >
                          <Icon
                            aria-hidden="true"
                            className="size-4 shrink-0 text-muted-foreground"
                          />
                          <span className="flex-1 truncate">{item.label}</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    );
                  })}
                  {modelFilters.map((item) => {
                    const Icon = getModelIcon(item.value);
                    return (
                      <SidebarMenuSubItem key={item.value}>
                        <SidebarMenuSubButton
                          isActive={activeModel === item.value}
                          render={
                            <Link
                              to={Routes.Root}
                              search={mergeImagePromptSearch(rawSearch, {
                                model: item.value,
                              })}
                              onClick={closeMobileSidebar}
                            />
                          }
                        >
                          <Icon
                            aria-hidden="true"
                            className="size-4 shrink-0 text-muted-foreground"
                          />
                          <span className="flex-1 truncate">{item.label}</span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    );
                  })}
                </SidebarMenuSub>
              )}
            </SidebarMenuItem>

            <SidebarMenuItem>
              <SidebarMenuButton
                onClick={() => setCategoriesOpen((value) => !value)}
              >
                <IconTags className="size-4 shrink-0" />
                <span className="truncate font-medium text-sm">
                  {copy.prompts}
                </span>
                <IconChevronRight
                  className={cn(
                    'ml-auto size-4 shrink-0 text-muted-foreground transition-transform',
                    showCategoriesSub && 'rotate-90'
                  )}
                />
              </SidebarMenuButton>

              {showCategoriesSub &&
                (() => {
                  const AllIcon = getCategoryIcon('all');
                  return (
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          isActive={activeCategory === 'all'}
                          render={
                            <Link
                              to={Routes.Root}
                              search={mergeImagePromptSearch(rawSearch, {
                                category: undefined,
                              })}
                              onClick={closeMobileSidebar}
                            />
                          }
                        >
                          <AllIcon
                            aria-hidden="true"
                            className="size-4 shrink-0 text-muted-foreground"
                          />
                          <span className="flex-1 truncate">
                            {getCategoryTitle('all', locale)}
                          </span>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>

                      {IMAGE_PROMPT_CATEGORIES.map((category) => {
                        const Icon = getCategoryIcon(category.slug);
                        return (
                          <SidebarMenuSubItem key={category.slug}>
                            <SidebarMenuSubButton
                              isActive={activeCategory === category.slug}
                              render={
                                <Link
                                  to={Routes.Root}
                                  search={mergeImagePromptSearch(rawSearch, {
                                    category: category.slug,
                                  })}
                                  onClick={closeMobileSidebar}
                                />
                              }
                            >
                              <Icon
                                aria-hidden="true"
                                className="size-4 shrink-0 text-muted-foreground"
                              />
                              <span className="flex-1 truncate">
                                {getCategoryTitle(category.slug, locale)}
                              </span>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        );
                      })}
                    </SidebarMenuSub>
                  );
                })()}
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </>
  );
}
